import { useMutation } from '@apollo/client';
import { Button, Dialog, DialogProps, Icon, SettingsGroup, SettingsItemTextField } from '@elipssolution/harfang';
import { mdiPencil } from '@mdi/js';
import { Stack, styled, Typography } from '@mui/material';
import { useEffect } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';

import { useConfirmationDialogContext } from '../../../components/ConfirmationDialogProvider';
import useFormat from '../../../hooks/useFormat';
import { SUCCESS_ANIMATION_DELAY } from '../../../utils/statusAnimationDelay';
import { UPDATE_USER, UpdateUserType, UpdateUserVariablesType } from '../../api/user';
import SettingsItemEmailField from '../../settings/SettingsItemEmailField';
import { UserType } from '../../types/user';
import { useSession } from '../session/SessionContext';

const HEADER_HEIGHT = 49;

const PageHeader = styled(Stack)(({ theme: { palette } }) => ({
	height: HEADER_HEIGHT,
	width: '100%',

	justifyContent: 'center',

	backgroundColor: palette.background.default,
}));

type FormType = Required<Pick<UserType, 'firstName' | 'lastName' | 'email'>>;

type ProfileProps = {
	open?: boolean;
	onClose?: () => void;
};

const Profile = ({ open = false, onClose }: ProfileProps) => {
	const { isMobileFormat } = useFormat();
	const { confirmAction } = useConfirmationDialogContext();

	const {
		user: { email, firstName, id, lastName },
	} = useSession();

	const {
		control,
		formState: { isValid, dirtyFields },
		handleSubmit,
		reset,
	} = useForm<FormType>({
		mode: 'onChange',
	});

	// Used to replace the useForm's isDirty not correctly updating when typing fast in the fields
	const formValues = useWatch({ control });

	const isDirty = JSON.stringify({ firstName, lastName, email }) !== JSON.stringify(formValues);

	const [
		updateUser,
		{ data: updateUserData, loading: isUpdateUserMutationLoading, reset: resetUpdateUserMutationStatus },
	] = useMutation<UpdateUserType, UpdateUserVariablesType>(UPDATE_USER, {
		refetchQueries: ['Session'],
	});

	const onSubmit = async (values: FormType) =>
		updateUser({
			variables: {
				updateUserInput: { id, ...values },
			},
		}).then(() => {
			reset(values);

			setTimeout(() => resetUpdateUserMutationStatus(), SUCCESS_ANIMATION_DELAY);
		});

	const handleClose = () => {
		resetUpdateUserMutationStatus();
		onClose?.();
	};

	const handleSubmitClick = async () => {
		if (dirtyFields.email) {
			const isEmailModificationConfirmed = await confirmAction({
				title: "Modification de l'adresse mail",
				content: `Attention ! En modifiant votre adresse mail, vous changez également votre identifiant de connexion.`,
				confirmButtonDetails: { label: 'Confirmer la modification' },
			});

			if (!isEmailModificationConfirmed) {
				return null;
			}
		}

		return handleSubmit(onSubmit)();
	};

	const actionsDialog: DialogProps['actionsDialog'] = [
		{
			id: 'close',
			disabled: isUpdateUserMutationLoading,
			label: 'Fermer',
			onClick: handleClose,
		},
		{
			id: 'update',
			disabled: !isDirty || !isValid,
			label: updateUserData ? 'Profil modifié' : 'Modifier',
			loading: isUpdateUserMutationLoading,
			onClick: handleSubmitClick,
			startIcon: <Icon path={mdiPencil} />,
			success: !!updateUserData,
			variant: 'contained',
		},
	];

	const formContent = (
		<SettingsGroup label="Informations" actionsWidth={isMobileFormat ? '100%' : undefined}>
			<Controller
				control={control}
				name="lastName"
				render={({ field: { onChange, ...restField } }) => {
					const handleUpperCaseChange = (value?: string) => onChange(value?.toUpperCase());

					return <SettingsItemTextField {...restField} label="Nom" onChange={handleUpperCaseChange} required />;
				}}
				rules={{ required: true }}
			/>
			<Controller
				control={control}
				name="firstName"
				render={({ field: { onChange, ...restField } }) => {
					const handleCaseChange = (value?: string) =>
						onChange(value ? `${value.charAt(0).toUpperCase()}${value.slice(1)}` : undefined);

					return <SettingsItemTextField {...restField} label="Prénom" onChange={handleCaseChange} required />;
				}}
				rules={{ required: true }}
			/>
			<SettingsItemEmailField
				control={control}
				description="Email de contact et identifiant de connexion."
				initialValue={email}
			/>
		</SettingsGroup>
	);

	// Initialize the form values. Not using defaultValues because if the fields are already populated when mounted, it cannot be emptied correctly, hence the timeout
	useEffect(() => {
		(open || isMobileFormat) &&
			setTimeout(() => {
				reset({
					firstName,
					lastName,
					email,
				});
			}, 1);
	}, [email, firstName, isMobileFormat, lastName, open, reset]);

	return isMobileFormat ? (
		<Stack alignItems="center" justifyContent="space-between" padding={2} height="100%">
			<Stack>
				<PageHeader>
					<Typography fontWeight="bold" variant="h6">
						Profil
					</Typography>
				</PageHeader>
				{formContent}
			</Stack>
			<Button
				disabled={!isDirty || !isValid}
				onClick={handleSubmitClick}
				startIcon={<Icon path={mdiPencil} />}
				variant="contained"
			>
				{updateUserData ? 'Profil modifié' : 'Modifier'}
			</Button>
		</Stack>
	) : (
		<Dialog open={open} onClose={handleClose} title="Profil" maxWidth="md" actionsDialog={actionsDialog} fullWidth>
			{formContent}
		</Dialog>
	);
};

export default Profile;
